import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { AuthorizationService } from '~ngx-shared/authentication';
import { MenuService } from '~ngx-shared/layout';
import { Aggregate } from '~ngx-shared/models';
import { ApplicationKindEnum } from '~shared/models';
import { AuthorizationUtil } from '~shared/utils';

@Injectable({
  providedIn: 'root'
})
export class PendingService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly menuService = inject(MenuService);
  private readonly apollo = inject(Apollo);

  private subscriptions: Subscription[] = [];

  // One badge per application kind; the alias matches the kind so results map back by name.
  private static readonly KINDS: ApplicationKindEnum[] = [
    ApplicationKindEnum.MEMBERSHIP,
    ApplicationKindEnum.MILITARY,
    ApplicationKindEnum.CONVERSION,
    ApplicationKindEnum.SCHOOL_ENROLMENT
  ];

  // Pending = arrived but not yet imported/approved. Hasura allows only one root field per
  // subscription, so we open one subscription per kind and update its badge independently.
  init(): void {
    this.authorizationService.currentRole$
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe(role => {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions = [];

        if (role && this.authorizationService.can(AuthorizationUtil.GROUP_MEMBERSHIP_ALL)) {
          this.subscriptions = PendingService.KINDS.map(kind =>
            this.apollo
              .subscribe<{ result?: Aggregate }>({
                query: gql`
                  subscription GetPendingApplications($kind: String!) {
                    result: external_application_aggregate(
                      where: { kind: { _eq: $kind }, status: { is_approved: { _eq: false } } }
                    ) {
                      aggregate {
                        count
                      }
                    }
                  }
                `,
                variables: { kind }
              })
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(queryResult => {
                this.setBadge(
                  ['/', 'actors', `${kind.replaceAll('_', '-')}-applications`],
                  queryResult.data?.result
                );
              })
          );
        }
      });
  }

  private setBadge(routerLink: string[], aggregate?: Aggregate): void {
    const menuItem = this.menuService.getItemFromRoute(routerLink);
    if (menuItem) {
      const count = aggregate?.aggregate?.count;
      menuItem.badge = count ? String(count) : undefined;
    }
  }
}
